/**
 * Offer-payment → daily amount and low-balance threshold helpers for
 * Daily Balance coloring / "Possible Missed Payments".
 */

/** Monthly payment ÷ this = daily (matches offer-calc business-day default). */
export const MONTHLY_TO_DAILY_DIVISOR = 20;
export const WEEKLY_TO_DAILY_DIVISOR = 5;
/** Base (non-offer) low-balance ceiling. */
export const BASE_LOW_BALANCE = 200;

/** Single amber band for all low-balance hits (weekends never use this). */
export type BalanceTone = "negative" | "low" | "normal";

export function parseLocalDate(dateStr: string): Date | null {
  const datePart = String(dateStr).slice(0, 10);
  const [year, month, day] = datePart.split("-").map(Number);
  if (!year || !month || !day) return null;
  const dt = new Date(year, month - 1, day);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

export function dateKeyLocal(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function isWeekendDate(date: Date): boolean {
  const day = date.getDay();
  return day === 0 || day === 6;
}

/**
 * Convert the offer-calc payment field to a daily amount.
 * Weekly → /5, monthly → /20, daily (and other) → as-is.
 */
export function paymentToDailyAmount(payment: number, frequency: string): number {
  const p = Number(payment);
  if (!Number.isFinite(p) || p <= 0) return 0;
  if (frequency === "weekly") return p / WEEKLY_TO_DAILY_DIVISOR;
  if (frequency === "monthly") return p / MONTHLY_TO_DAILY_DIVISOR;
  return p;
}

/** threshold = dailyPayment + max(200, 0.10 * dailyPayment) */
export function dynamicLowBalanceThreshold(dailyPayment: number): number {
  const daily = Number(dailyPayment);
  if (!Number.isFinite(daily) || daily <= 0) return BASE_LOW_BALANCE;
  return daily + Math.max(BASE_LOW_BALANCE, 0.1 * daily);
}

export interface BalancePointLike {
  date: string;
  balance: number;
}

/**
 * Drop current-month (and future) balance rows so MoneyThumb trailing stubs
 * don't inflate coloring / missed-payment counts.
 */
export function balancesBeforeCurrentMonth(data: BalancePointLike[]): BalancePointLike[] {
  const now = new Date();
  const firstOfCurrentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  return data.filter((d) => {
    const pointDate = parseLocalDate(d.date);
    if (!pointDate) return false;
    return pointDate < firstOfCurrentMonth;
  });
}

/** Last `term` business days (weekends excluded) in the series, as YYYY-MM-DD keys. */
export function lastBusinessDayKeys(data: BalancePointLike[], term: number): Set<string> {
  const n = Math.floor(Number(term));
  if (!Number.isFinite(n) || n <= 0) return new Set();

  const businessKeys: string[] = [];
  for (const point of data) {
    const dt = parseLocalDate(point.date);
    if (!dt || isWeekendDate(dt)) continue;
    businessKeys.push(dateKeyLocal(dt));
  }
  businessKeys.sort();
  return new Set(businessKeys.slice(-n));
}

/**
 * Tone for a balance cell.
 * Weekends never get low-balance amber (caller should pass weekend: true).
 * With payment + dynamicDay: use dynamic threshold; otherwise base 0–200.
 */
export function balanceTone(
  balance: number,
  opts?: {
    dailyPayment?: number | null;
    dynamicDay?: boolean;
    weekend?: boolean;
  }
): BalanceTone {
  if (balance < 0) return "negative";
  // Weekends keep grey weekend bg only — never low-balance amber.
  if (opts?.weekend) return "normal";

  const daily = opts?.dailyPayment;
  const hasDynamic = opts?.dynamicDay === true && daily != null && daily > 0;
  if (hasDynamic) {
    const threshold = dynamicLowBalanceThreshold(daily);
    if (balance <= threshold) return "low";
    return "normal";
  }

  if (balance <= BASE_LOW_BALANCE) return "low";
  return "normal";
}

/**
 * Count last-term business days where 0 ≤ balance ≤ daily payment.
 * Uses the payment alone (no $200 / 10% cushion — that buffer is only for
 * low-balance amber coloring via {@link dynamicLowBalanceThreshold}).
 */
export function countPossibleMissedPayments(
  data: BalancePointLike[],
  dailyPayment: number,
  term: number
): number {
  if (!(dailyPayment > 0) || !(term > 0)) return 0;
  const window = lastBusinessDayKeys(data, term);
  if (window.size === 0) return 0;

  let count = 0;
  for (const point of data) {
    const dt = parseLocalDate(point.date);
    if (!dt) continue;
    const key = dateKeyLocal(dt);
    if (!window.has(key)) continue;
    if (point.balance >= 0 && point.balance <= dailyPayment) count += 1;
  }
  return count;
}

/**
 * Possible-missed count from raw daily balances + live offer payment fields.
 * Applies the same current-month filter as Daily Balances.
 */
export function countPossibleMissedFromOffer(
  data: BalancePointLike[],
  offerPayment: number | null | undefined,
  offerFrequency: string | null | undefined,
  offerTerm: number | null | undefined
): number {
  if (offerPayment == null || !(Number(offerPayment) > 0)) return 0;
  const daily = paymentToDailyAmount(Number(offerPayment), offerFrequency ?? "daily");
  const term = Math.floor(Number(offerTerm));
  if (!(daily > 0) || !Number.isFinite(term) || term <= 0) return 0;
  return countPossibleMissedPayments(balancesBeforeCurrentMonth(data), daily, term);
}
